import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth, useAuthenticatedFetch } from './AuthContext';
import { useSettings } from './SettingsContext';
import { usePlan } from './PlanContext';
import { useClients } from './ClientContext';
import { safeJsonParse } from '../utils/sanitize';
import type { Post } from '../types';

const API_URL = import.meta.env.VITE_API_URL || 'https://doopl-api.joyburhaan.workers.dev';

interface PostsContextValue {
  posts: Post[];
  isLoading: boolean;
  error: string | null;
  addPost: (post: Omit<Post, 'id'>) => Promise<Post | null>;
  updatePost: (id: string, updates: Partial<Post>) => Promise<boolean>;
  deletePost: (id: string) => Promise<boolean>;
  getPost: (id: string) => Post | undefined;
  refreshPosts: () => Promise<void>;
  // Approval methods (these call the API)
  generateApprovalToken: (postId: string) => Promise<string | null>;
  // Public approval methods (no auth required)
  getPostByToken: (token: string) => Promise<{ post: Post | null; expired: boolean; valid: boolean; showWatermark: boolean }>;
  approvePost: (token: string, approverName?: string) => Promise<boolean>;
  requestChanges: (token: string, comment: string) => Promise<boolean>;
  // Batch approval (simplified - uses localStorage for now)
  generateBatchApprovalToken: (postIds: string[]) => string;
  getBatchByToken: (token: string) => { posts: Post[]; expired: boolean; valid: boolean; showWatermark: boolean };
}

const PostsContext = createContext<PostsContextValue | undefined>(undefined);

// Helper to convert API post to frontend Post
function parsePost(apiPost: Record<string, unknown>): Post {
  return {
    id: apiPost.id as string,
    title: apiPost.title as string,
    source: (apiPost.source as string) || '',
    platforms: safeJsonParse(apiPost.platforms, []),
    captions: safeJsonParse(apiPost.captions, []),
    mediaType: (apiPost.media_type as Post['mediaType']) || 'text',
    mediaItems: safeJsonParse(apiPost.media_items, []),
    platformMedia: safeJsonParse(apiPost.platform_media, undefined),
    thumbnailUrl: apiPost.thumbnail_url as string | undefined,
    status: (apiPost.status as Post['status']) || 'unscheduled',
    scheduledDate: apiPost.scheduled_date ? new Date(apiPost.scheduled_date as string) : undefined,
    scheduledTime: apiPost.scheduled_time as string | undefined,
    approvalToken: apiPost.approval_token as string | undefined,
    approvedAt: apiPost.approved_at ? new Date(apiPost.approved_at as string) : undefined,
    approvedBy: apiPost.approved_by as string | undefined,
    comments: safeJsonParse(apiPost.comments, undefined),
    mediaCleaned: apiPost.media_cleaned === 1,
    mediaCleanedAt: apiPost.media_cleaned_at ? new Date(apiPost.media_cleaned_at as string) : undefined,
    clientId: apiPost.client_id as string | undefined,
  };
}

// Helper to convert frontend Post to API format
function serializePost(post: Partial<Post>): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (post.title !== undefined) result.title = post.title;
  if (post.platforms !== undefined) result.platforms = JSON.stringify(post.platforms);
  if (post.captions !== undefined) result.captions = JSON.stringify(post.captions);
  if (post.mediaType !== undefined) result.media_type = post.mediaType;
  if (post.mediaItems !== undefined) result.media_items = JSON.stringify(post.mediaItems);
  if (post.platformMedia !== undefined) result.platform_media = JSON.stringify(post.platformMedia);
  if (post.thumbnailUrl !== undefined) result.thumbnail_url = post.thumbnailUrl;
  if (post.status !== undefined) result.status = post.status;
  if (post.scheduledDate !== undefined) {
    result.scheduled_date = post.scheduledDate instanceof Date
      ? post.scheduledDate.toISOString()
      : post.scheduledDate;
  }
  if (post.scheduledTime !== undefined) result.scheduled_time = post.scheduledTime;
  if (post.comments !== undefined) result.comments = JSON.stringify(post.comments);
  if (post.clientId !== undefined) result.client_id = post.clientId;

  return result;
}

// Batch token storage (still localStorage based for simplicity)
const BATCH_TOKENS_KEY = 'doopl_batch_tokens';

interface BatchToken {
  token: string;
  postIds: string[];
  createdAt: number;
  expiresAt: number;
  showWatermark: boolean;
}

function getBatchTokens(): BatchToken[] {
  return safeJsonParse(localStorage.getItem(BATCH_TOKENS_KEY), []);
}

function saveBatchToken(batchToken: BatchToken) {
  const tokens = getBatchTokens().filter(t => t.token !== batchToken.token);
  tokens.push(batchToken);
  localStorage.setItem(BATCH_TOKENS_KEY, JSON.stringify(tokens));
}

export function PostsProvider({ children }: { children: ReactNode }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { isAuthenticated, token } = useAuth();
  const { settings } = useSettings();
  const { showWatermark: shouldShowWatermark } = usePlan();
  const { activeClientId } = useClients();
  const authFetch = useAuthenticatedFetch();

  // Load posts when authenticated (filtered by active client)
  const refreshPosts = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build query params for client filtering
      const params = new URLSearchParams();
      if (activeClientId === 'personal') {
        params.set('clientId', 'personal');
      } else if (activeClientId) {
        params.set('clientId', activeClientId);
      }
      // If activeClientId is null, fetch all posts (no filter)

      const queryString = params.toString();
      const url = queryString ? `/posts?${queryString}` : '/posts';

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setPosts((data.posts || []).map(parsePost));
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to load posts');
      }
    } catch (err) {
      console.error('Failed to load posts:', err);
      setError('Network error loading posts');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, token, authFetch, activeClientId]);

  useEffect(() => {
    refreshPosts();
  }, [refreshPosts]);

  const addPost = useCallback(async (post: Omit<Post, 'id'>): Promise<Post | null> => {
    try {
      // Auto-assign to active client (unless explicitly set)
      const postData = { ...post } as Partial<Post>;
      if (postData.clientId === undefined && activeClientId && activeClientId !== 'personal') {
        postData.clientId = activeClientId;
      } else if (activeClientId === 'personal') {
        // Explicitly set to null for personal posts
        postData.clientId = undefined;
      }

      const response = await authFetch('/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serializePost(postData)),
      });

      if (response.ok) {
        const data = await response.json();
        const newPost = parsePost(data.post);
        setPosts(prev => [newPost, ...prev]);
        return newPost;
      }

      console.error('Failed to create post:', await response.text());
      return null;
    } catch (err) {
      console.error('Failed to create post:', err);
      return null;
    }
  }, [authFetch, activeClientId]);

  const updatePost = useCallback(async (id: string, updates: Partial<Post>): Promise<boolean> => {
    try {
      const response = await authFetch(`/posts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serializePost(updates)),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedPost = parsePost(data.post);
        setPosts(prev => prev.map(p => p.id === id ? updatedPost : p));
        return true;
      }

      console.error('Failed to update post:', await response.text());
      return false;
    } catch (err) {
      console.error('Failed to update post:', err);
      return false;
    }
  }, [authFetch]);

  const deletePost = useCallback(async (id: string): Promise<boolean> => {
    try {
      const response = await authFetch(`/posts/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setPosts(prev => prev.filter(p => p.id !== id));
        return true;
      }

      console.error('Failed to delete post:', await response.text());
      return false;
    } catch (err) {
      console.error('Failed to delete post:', err);
      return false;
    }
  }, [authFetch]);

  const getPost = useCallback((id: string): Post | undefined => {
    return posts.find(post => post.id === id);
  }, [posts]);

  const generateApprovalToken = useCallback(async (postId: string): Promise<string | null> => {
    try {
      const response = await authFetch(`/posts/${postId}/approval-token`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        // Update local state
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, approvalToken: data.token, status: 'pending_approval' as const }
            : p
        ));
        return data.token;
      }

      console.error('Failed to generate approval token:', await response.text());
      return null;
    } catch (err) {
      console.error('Failed to generate approval token:', err);
      return null;
    }
  }, [authFetch]);

  // Public method - no auth required
  const getPostByToken = useCallback(async (tokenStr: string): Promise<{
    post: Post | null;
    expired: boolean;
    valid: boolean;
    showWatermark: boolean;
  }> => {
    try {
      const response = await fetch(`${API_URL}/approve/${tokenStr}`);

      if (response.ok) {
        const data = await response.json();
        return {
          post: data.post ? parsePost(data.post) : null,
          expired: false,
          valid: true,
          showWatermark: data.showWatermark || false,
        };
      }

      const errData = await response.json();
      return {
        post: null,
        expired: errData.error === 'Approval link has expired',
        valid: false,
        showWatermark: false,
      };
    } catch (err) {
      console.error('Failed to get post by token:', err);
      return { post: null, expired: false, valid: false, showWatermark: false };
    }
  }, []);

  // Public method - no auth required
  const approvePost = useCallback(async (tokenStr: string, approverName?: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/approve/${tokenStr}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: true,
          approverName: approverName || 'Approver',
        }),
      });

      return response.ok;
    } catch (err) {
      console.error('Failed to approve post:', err);
      return false;
    }
  }, []);

  // Public method - no auth required
  const requestChanges = useCallback(async (tokenStr: string, comment: string): Promise<boolean> => {
    try {
      const response = await fetch(`${API_URL}/approve/${tokenStr}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: false,
          comment,
        }),
      });

      return response.ok;
    } catch (err) {
      console.error('Failed to request changes:', err);
      return false;
    }
  }, []);

  // Batch approval (simplified - uses localStorage)
  const generateBatchApprovalToken = useCallback((postIds: string[]): string => {
    const token = crypto.randomUUID();
    const expiryDays = settings.linkExpiryDays || 7;
    const batchToken: BatchToken = {
      token,
      postIds,
      createdAt: Date.now(),
      expiresAt: expiryDays === 0 ? 0 : Date.now() + expiryDays * 24 * 60 * 60 * 1000,
      showWatermark: shouldShowWatermark(),
    };
    saveBatchToken(batchToken);

    // Optimistically update local state immediately for better UX
    setPosts(prev => prev.map(p =>
      postIds.includes(p.id) ? { ...p, status: 'pending_approval' as const } : p
    ));

    // Sync with server in background - errors are logged but don't block UI
    Promise.all(
      postIds.map(postId => updatePost(postId, { status: 'pending_approval' }))
    ).catch(err => console.error('Failed to sync batch post statuses with server:', err));

    return token;
  }, [settings.linkExpiryDays, shouldShowWatermark, updatePost]);

  const getBatchByToken = useCallback((tokenStr: string): {
    posts: Post[];
    expired: boolean;
    valid: boolean;
    showWatermark: boolean;
  } => {
    const batchTokens = getBatchTokens();
    const batchToken = batchTokens.find(t => t.token === tokenStr);

    if (!batchToken) {
      return { posts: [], expired: false, valid: false, showWatermark: false };
    }

    // Check expiry
    if (batchToken.expiresAt !== 0 && Date.now() > batchToken.expiresAt) {
      return { posts: [], expired: true, valid: false, showWatermark: false };
    }

    const batchPosts = batchToken.postIds
      .map(id => posts.find(p => p.id === id))
      .filter((p): p is Post => p !== undefined);

    return {
      posts: batchPosts,
      expired: false,
      valid: batchPosts.length > 0,
      showWatermark: batchToken.showWatermark,
    };
  }, [posts]);

  return (
    <PostsContext.Provider
      value={{
        posts,
        isLoading,
        error,
        addPost,
        updatePost,
        deletePost,
        getPost,
        refreshPosts,
        generateApprovalToken,
        getPostByToken,
        approvePost,
        requestChanges,
        generateBatchApprovalToken,
        getBatchByToken,
      }}
    >
      {children}
    </PostsContext.Provider>
  );
}

// Default value for when context isn't available yet (during initial render)
const DEFAULT_POSTS_VALUE: PostsContextValue = {
  posts: [],
  isLoading: true,
  error: null,
  addPost: async () => null,
  updatePost: async () => false,
  deletePost: async () => false,
  getPost: () => undefined,
  refreshPosts: async () => {},
  generateApprovalToken: async () => null,
  getPostByToken: async () => ({ post: null, expired: false, valid: false, showWatermark: false }),
  approvePost: async () => false,
  requestChanges: async () => false,
  generateBatchApprovalToken: () => '',
  getBatchByToken: () => ({ posts: [], expired: false, valid: false, showWatermark: false }),
};

export function usePosts() {
  const context = useContext(PostsContext);
  // Return default value if context not ready (prevents crashes during initial render)
  return context || DEFAULT_POSTS_VALUE;
}
